const SMART_CAPTCHA_CONTAINER_SELECTOR = '[data-smart-captcha]';
const SMART_CAPTCHA_WAIT_TIMEOUT = 5000;

function getCaptchaApi() {
  return window.smartCaptcha;
}

function waitForCaptchaApi(timeout = SMART_CAPTCHA_WAIT_TIMEOUT) {
  const api = getCaptchaApi();
  if (api) {
    return Promise.resolve(api);
  }

  return new Promise((resolve) => {
    const startedAt = Date.now();
    const timer = window.setInterval(() => {
      const currentApi = getCaptchaApi();
      if (currentApi) {
        window.clearInterval(timer);
        resolve(currentApi);
        return;
      }

      if (Date.now() - startedAt >= timeout) {
        window.clearInterval(timer);
        resolve(null);
      }
    }, 100);
  });
}

function getCaptchaContainer(form) {
  return form.querySelector(SMART_CAPTCHA_CONTAINER_SELECTOR);
}

function isProtectedForm(form) {
  return Boolean(getCaptchaContainer(form));
}

function ensureHiddenTokenInput(form) {
  let input = form.querySelector('input[name="smart-token"]');
  if (!input) {
    input = document.createElement('input');
    input.type = 'hidden';
    input.name = 'smart-token';
    form.appendChild(input);
  }
  return input;
}

function syncToken(form) {
  const container = getCaptchaContainer(form);
  if (!container) {
    return '';
  }

  const widgetId = container.dataset.smartCaptchaWidgetId;
  const api = getCaptchaApi();
  const token = widgetId && api ? api.getResponse(widgetId) : '';
  ensureHiddenTokenInput(form).value = token || '';
  return token || '';
}

export function refreshSmartCaptchaToken(form) {
  if (!isProtectedForm(form)) {
    return '';
  }

  return syncToken(form);
}

function resetPendingState(form) {
  delete form.dataset.smartCaptchaPendingSubmit;
}

function renderCaptcha(container) {
  const api = getCaptchaApi();
  const sitekey = container.dataset.sitekey;
  const containerId = container.id;
  const isInvisible = container.dataset.invisible === 'true';

  if (!api || !sitekey || !containerId || container.dataset.smartCaptchaWidgetId) {
    return;
  }

  const widgetId = api.render(containerId, {
    sitekey,
    invisible: isInvisible,
    hideShield: false,
    callback: () => {
      const form = container.closest('form');
      if (!form) {
        return;
      }

      syncToken(form);
      if (form.dataset.smartCaptchaPendingSubmit === 'true') {
        resetPendingState(form);
        form.dispatchEvent(new CustomEvent('smart-captcha-ready'));
      }
    },
    'expired-callback': () => {
      const form = container.closest('form');
      if (!form) {
        return;
      }

      ensureHiddenTokenInput(form).value = '';
      resetPendingState(form);
    },
    'error-callback': () => {
      const form = container.closest('form');
      if (!form) {
        return;
      }

      ensureHiddenTokenInput(form).value = '';
      resetPendingState(form);
    }
  });

  container.dataset.smartCaptchaWidgetId = String(widgetId);
}

export function initSmartCaptchas(root = document) {
  root.querySelectorAll(SMART_CAPTCHA_CONTAINER_SELECTOR).forEach((container) => {
    renderCaptcha(container);
  });
}

document.addEventListener('DOMContentLoaded', () => {
  initSmartCaptchas(document);
});

document.addEventListener('DOMContentMutated', () => {
  initSmartCaptchas(document);
});

document.addEventListener('success', (event) => {
  const form = event.detail && event.detail.form;
  if (!form || !isProtectedForm(form)) {
    return;
  }

  const container = getCaptchaContainer(form);
  const widgetId = container && container.dataset.smartCaptchaWidgetId;
  const api = getCaptchaApi();
  const hiddenInput = form.querySelector('input[name="smart-token"]');

  if (hiddenInput) {
    hiddenInput.value = '';
  }

  if (widgetId && api) {
    api.reset(widgetId);
  }
});

export async function runInvisibleSmartCaptcha(form) {
  if (!isProtectedForm(form)) {
    return true;
  }

  const api = await waitForCaptchaApi();
  if (!api) {
    return false;
  }

  initSmartCaptchas(form);

  const container = getCaptchaContainer(form);
  const widgetId = container && container.dataset.smartCaptchaWidgetId;
  if (!widgetId) {
    return false;
  }

  const token = syncToken(form);
  if (token) {
    return true;
  }

  return new Promise((resolve) => {
    const readyHandler = () => {
      form.removeEventListener('smart-captcha-ready', readyHandler);
      resolve(Boolean(syncToken(form)));
    };

    form.addEventListener('smart-captcha-ready', readyHandler, { once: true });
    form.dataset.smartCaptchaPendingSubmit = 'true';
    api.execute(widgetId);
  });
}

document.addEventListener('submit', async (event) => {
  const form = event.target;
  if (!(form instanceof HTMLFormElement) || !form.matches('[data-smart-captcha-native-form]')) {
    return;
  }

  if (form.dataset.smartCaptchaSubmitting === 'true') {
    delete form.dataset.smartCaptchaSubmitting;
    return;
  }

  event.preventDefault();

  const isCaptchaReady = await runInvisibleSmartCaptcha(form);
  if (!isCaptchaReady) {
    return;
  }

  form.dataset.smartCaptchaSubmitting = 'true';
  form.submit();
}, true);
